import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Radio, Activity, Users, ClipboardList, Video, Gauge, ListOrdered, Sparkles, Target, ArrowRight } from "lucide-react";

const FEATURES = [
  { icon: Activity, label: "Live Game Scoring" },
  { icon: Users, label: "Team Management" },
  { icon: ListOrdered, label: "Player Statistics" },
  { icon: ClipboardList, label: "Live Box Scores" },
  { icon: Gauge, label: "Pitch-by-Pitch Tracking" },
  { icon: Video, label: "Live Streaming" },
  { icon: Radio, label: "Pocket Radar Integration" },
  { icon: Sparkles, label: "Play-by-Play" },
  { icon: Users, label: "Fan Experience" },
  { icon: Target, label: "Recruiting Integration" },
];

export default function CBRLive() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative bg-[#0A192F] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #E63946 0, transparent 40%), radial-gradient(circle at 80% 60%, #E63946 0, transparent 40%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-24 lg:py-32 text-center">
          <div className="inline-flex items-center gap-2 bg-[#E63946] text-white text-[11px] font-semibold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
            <Radio className="w-3.5 h-3.5" /> Coming Soon
          </div>
          <h1 data-testid="cbr-live-headline" className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter mt-6">CBR Live</h1>
          <p data-testid="cbr-live-subhead" className="text-lg md:text-xl text-white/80 mt-4 max-w-2xl mx-auto">The Future of Canadian Baseball Game Coverage</p>
          <p className="text-base text-white/60 mt-8 max-w-2xl mx-auto leading-relaxed">
            CBR Live is our next-generation live scoring and game management platform currently in development. Our vision is to combine every part of game day into one connected experience.
          </p>
        </div>
      </section>

      {/* Features grid */}
      <section className="bg-[#F1F3F5] py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-[#E63946] text-center">One Platform · Every Game</div>
          <h2 className="text-center font-display text-3xl uppercase tracking-tight mt-2">What CBR Live Will Bring</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-10">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`cbr-live-feature-${i}`} className="bg-white border border-slate-200 rounded-sm p-5 flex flex-col items-center text-center gap-3 hover:border-[#E63946] transition-colors">
                <div className="w-11 h-11 rounded-sm bg-[#0A192F] grid place-items-center"><f.icon className="w-5 h-5 text-[#E63946]" /></div>
                <span className="text-sm font-semibold text-[#0A192F] leading-tight">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Connected ecosystem */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl uppercase tracking-tight">More Than Scorekeeping</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Unlike traditional scorekeeping applications, every game scored through CBR Live will contribute directly to player development and recruiting — automatically updating <span className="font-semibold text-[#0A192F]">player profiles, career timelines, rankings, awards, and future leaderboards</span>.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {["CBR Prospects", "CBR Nation", "Recruiter Portal", "CBR Live"].map((p) => (
              <span key={p} className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm border ${p === "CBR Live" ? "bg-[#E63946] text-white border-[#E63946]" : "border-slate-300 text-[#0A192F]"}`}>{p}</span>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/prospects" data-testid="cbr-live-cta" className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm hover:bg-[#1a2a4a] transition-colors">
              Explore CBR Prospects Today <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-slate-400 mt-6 uppercase tracking-[0.2em]">CBR Live · Coming Soon</p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
